import React, { useState, useEffect } from "react";
import "./CareerJourneyFlow.css"; // Re-use existing styles where possible
import { X } from "lucide-react";

const LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];

const SkillsEditor = ({ isOpen, onClose, onSave, rolesData }) => {
  const [editedRoles, setEditedRoles] = useState([]);

  useEffect(() => {
    if (isOpen && rolesData) {
      // Deep copy to avoid mutating props directly
      setEditedRoles(JSON.parse(JSON.stringify(rolesData)));
    }
  }, [isOpen, rolesData]);

  const handleLevelChange = (roleIndex, skillName, newLevelIndex) => {
    const updatedRoles = [...editedRoles];
    const role = updatedRoles[roleIndex];

    // Find if skill exists in the role's selected skills
    // The structure of rolesData is expected to be:
    // [{ roleId, roleName, availableSkills: { skills: [], specific_skills: {} }, selectedSkills: [...] }]

    // We need to update the 'selectedSkills' part of the state
    // If it's not selected, we add it.

    // Wait, let's clarify the internal state structure.
    // We should probably maintain a structure that is easy to render and then convert back on save.
    // Let's assume editedRoles mirrors the structure passed in.

    // For the UI, we iterate over availableSkills.
    // We need to check if a skill is "active" (present in selectedSkills).

    // Let's simplify: The parent will pass a structure that combines available and selected.
    // rolesData = [
    //   {
    //     roleId,
    //     roleName,
    //     skills: [
    //       { name: "Accounting Standards", level: "Intermediate", subSkills: ["IFRS"], allSubSkills: ["IFRS", "US GAAP", ...], isSelected: true }
    //     ]
    //   }
    // ]

    const skill = role.skills.find((s) => s.name === skillName);
    if (skill) {
      skill.level = LEVELS[newLevelIndex];
      if (!skill.isSelected) {
        skill.isSelected = true; // Auto-select if level changes
      }
    }
    setEditedRoles(updatedRoles);
  };

  const handleSubSkillToggle = (roleIndex, skillName, subSkillName) => {
    const updatedRoles = [...editedRoles];
    const role = updatedRoles[roleIndex];
    const skill = role.skills.find((s) => s.name === skillName);

    if (skill) {
      if (!skill.subSkills) skill.subSkills = [];

      const existingIndex = skill.subSkills.findIndex((s) =>
        typeof s === "string" ? s === subSkillName : s.name === subSkillName,
      );

      if (existingIndex > -1) {
        skill.subSkills.splice(existingIndex, 1);
      } else {
        skill.subSkills.push({ name: subSkillName, level: "Intermediate" });
      }

      // Auto-select skill if sub-skill is checked
      if (!skill.isSelected) {
        skill.isSelected = true;
        if (!skill.level) skill.level = "Intermediate";
      }
    }
    setEditedRoles(updatedRoles);
  };

  const handleSubSkillLevelChange = (
    roleIndex,
    skillName,
    subSkillName,
    newLevel,
  ) => {
    const updatedRoles = [...editedRoles];
    const role = updatedRoles[roleIndex];
    const skill = role.skills.find((s) => s.name === skillName);

    if (skill && skill.subSkills) {
      const subSkill = skill.subSkills.find((s) =>
        typeof s === "string" ? s === subSkillName : s.name === subSkillName,
      );
      if (subSkill) {
        if (typeof subSkill === "string") {
          // Upgrade to object if it was a string
          const index = skill.subSkills.indexOf(subSkill);
          skill.subSkills[index] = { name: subSkill, level: newLevel };
        } else {
          subSkill.level = newLevel;
        }
      }
    }
    setEditedRoles(updatedRoles);
  };

  const handleSkillToggle = (roleIndex, skillName) => {
    const updatedRoles = [...editedRoles];
    const role = updatedRoles[roleIndex];
    const skill = role.skills.find((s) => s.name === skillName);

    if (skill) {
      skill.isSelected = !skill.isSelected;
      // If selecting, set default level if missing
      if (skill.isSelected && !skill.level) {
        skill.level = "Intermediate";
      }
    }
    setEditedRoles(updatedRoles);
  };

  const handleSave = () => {
    onSave(editedRoles);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 3000,
        overflowY: "auto",
        backgroundColor: "rgba(0,0,0,0.3)",
      }}
    >
      <div
        className="modal-content"
        style={{
          maxWidth: "800px",
          margin: "50px auto",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor: "#fff",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        <div className="modal-header-top">
          <h2 className="modal-title">Edit Skills</h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <X size={24} />
          </button>
        </div>

        <div
          className="modal-body"
          style={{ padding: "32px 40px", overflowY: "auto", flex: 1 }}
        >
          {editedRoles.map((role, roleIndex) => (
            <div
              key={role.roleId}
              className="cj-section"
              style={{ marginBottom: "40px" }}
            >
              <h3 style={{ borderBottom: "none", marginBottom: "10px" }}>
                {role.roleName}
              </h3>
              <p
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#666",
                  marginBottom: "20px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    marginTop: "8px",
                    color: "#c53030",
                    fontWeight: "500",
                  }}
                >
                  Important: Any changes made here will directly update the
                  candidate's skills.
                </span>
              </p>

              {role.skills.map((skill) => (
                <div
                  key={skill.name}
                  className="skill-editor-item"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "16px",
                    backgroundColor: skill.isSelected ? "#fff" : "#f8fafc",
                    opacity: skill.isSelected ? 1 : 0.8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "16px",
                      cursor: "pointer",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                    onClick={(e) => {
                      if (e.target.type !== "checkbox") {
                        handleSkillToggle(roleIndex, skill.name);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={skill.isSelected}
                      onChange={() => handleSkillToggle(roleIndex, skill.name)}
                      style={{
                        width: "20px",
                        height: "20px",
                        marginRight: "12px",
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        color: "#1e293b",
                        userSelect: "none",
                      }}
                    >
                      {skill.name}
                    </span>
                  </div>

                  {skill.isSelected && (
                    <div style={{ paddingLeft: "32px" }}>
                      {/* Level Slider */}
                      <div
                        style={{
                          marginBottom: "20px",
                          paddingRight: "10px",
                          paddingLeft: "2px",
                        }}
                      >
                        <label>Skill Level</label>
                        <input
                          type="range"
                          min="0"
                          max="3"
                          step="1"
                          value={LEVELS.indexOf(skill.level || "Intermediate")}
                          onChange={(e) =>
                            handleLevelChange(
                              roleIndex,
                              skill.name,
                              parseInt(e.target.value),
                            )
                          }
                          style={{
                            width: "100%",
                            cursor: "pointer",
                            accentColor: "#667eea",
                            boxShadow: "none",
                            padding: 0,
                            margin: 0,
                            display: "block",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "4px",
                            fontSize: "0.8rem",
                            color: "#64748b",
                          }}
                        >
                          {LEVELS.map((l, i) => (
                            <span
                              key={l}
                              style={{
                                fontWeight:
                                  LEVELS.indexOf(
                                    skill.level || "Intermediate",
                                  ) === i
                                    ? "bold"
                                    : "normal",
                                color:
                                  LEVELS.indexOf(
                                    skill.level || "Intermediate",
                                  ) === i
                                    ? "#667eea"
                                    : "inherit",
                                width: "25%", // Distribute space evenly
                                textAlign:
                                  i === 0
                                    ? "left"
                                    : i === LEVELS.length - 1
                                      ? "right"
                                      : "center",
                              }}
                            >
                              {l}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Sub-skills */}
                      {skill.allSubSkills && skill.allSubSkills.length > 0 && (
                        <div style={{ marginTop: "24px" }}>
                          <label>Sub-skills</label>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {skill.allSubSkills.map((subName) => {
                              const selectedSub = skill.subSkills?.find((s) =>
                                typeof s === "string"
                                  ? s === subName
                                  : s.name === subName,
                              );
                              const isSubSelected = !!selectedSub;
                              const currentSubLevel =
                                typeof selectedSub === "object"
                                  ? selectedSub.level
                                  : "Intermediate";

                              return (
                                <div
                                  key={subName}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                      userSelect: "none",
                                      WebkitTapHighlightColor: "transparent",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSubSelected}
                                      onChange={() =>
                                        handleSubSkillToggle(
                                          roleIndex,
                                          skill.name,
                                          subName,
                                        )
                                      }
                                      style={{
                                        width: "18px",
                                        height: "18px",
                                        marginRight: "12px",
                                        cursor: "pointer",
                                        accentColor: "#667eea",
                                      }}
                                    />
                                    {subName}
                                  </label>

                                  {isSubSelected && (
                                    <div
                                      style={{
                                        paddingLeft: "30px",
                                        maxWidth: "450px",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <input
                                        type="range"
                                        min="0"
                                        max="3"
                                        step="1"
                                        value={LEVELS.indexOf(currentSubLevel)}
                                        onChange={(e) =>
                                          handleSubSkillLevelChange(
                                            roleIndex,
                                            skill.name,
                                            subName,
                                            LEVELS[parseInt(e.target.value)],
                                          )
                                        }
                                        style={{
                                          width: "100%",
                                          cursor: "pointer",
                                          accentColor: "#667eea",
                                          display: "block",
                                          height: "4px",
                                          boxShadow: "none",
                                          padding: 0,
                                          margin: 0,
                                        }}
                                      />
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          marginTop: "8px",
                                          fontSize: "0.75rem",
                                          color: "#94a3b8",
                                        }}
                                      >
                                        {LEVELS.map((l, i) => (
                                          <span
                                            key={l}
                                            style={{
                                              fontWeight:
                                                currentSubLevel === l
                                                  ? "bold"
                                                  : "normal",
                                              color:
                                                currentSubLevel === l
                                                  ? "#667eea"
                                                  : "inherit",
                                              textAlign:
                                                i === 0
                                                  ? "left"
                                                  : i === LEVELS.length - 1
                                                    ? "right"
                                                    : "center",
                                              flex: 1,
                                            }}
                                          >
                                            {l}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="modal-actions-v2">
          <button className="skill-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="skill-save-btn" onClick={handleSave}>
            Save Skills
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillsEditor;
